import ValibotCommon from '@shared-schemas/common'
import * as v from 'valibot'

type GenerateSchemaParams = Pick<
	Parameters<typeof ValibotCommon.getUuidSchema_V2>[0],
	'expected_format' | 'convert_format'
>

export const getIsOptimusEnabledSchema = () => {
	return v.object({
		enabled: v.boolean(),
	})
}

// ==========================================================
// ====================== TOD Schedule ======================
// ==========================================================

export const tms_tod_schedule_data = v.object({
	days: v.object({
		monday: v.boolean(),
		tuesday: v.boolean(),
		wednesday: v.boolean(),
		thursday: v.boolean(),
		friday: v.boolean(),
		saturday: v.boolean(),
		sunday: v.boolean(),
	}),
	months: v.optional(
		v.object({
			january: v.boolean(),
			february: v.boolean(),
			march: v.boolean(),
			april: v.boolean(),
			may: v.boolean(),
			june: v.boolean(),
			july: v.boolean(),
			august: v.boolean(),
			september: v.boolean(),
			october: v.boolean(),
			november: v.boolean(),
			december: v.boolean(),
		}),
	),
	days_of_month: v.optional(v.record(v.string(), v.boolean())),
	// TODO: Get Picklist working as a type
	// mode: v.picklist(['coordinated', 'free', 'actuated-free', 'flash']),
	mode: v.string(),
	local_start_time: v.string(),
	local_end_time: v.string(),
})

export const getOptimusTmsTodSchema = (params: GenerateSchemaParams) => {
	return v.object({
		time_of_day_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'time_of_day_id' }),
		atlas_signal_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'atlas_signal_id' }),
		name: v.string(),
		time_of_day_data: tms_tod_schedule_data,
		coordination_settings_id: v.nullable(
			ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'coordination_settings_id' }),
		),
		phase_settings_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'phase_settings_id' }),
		sequence_id: v.nullable(ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'sequence_id' })),
		valid_range: v.string(),
		coordination_settings_external_id: v.nullable(v.string()),
		phase_settings_external_id: v.string(),
		sequence_external_id: v.nullable(v.string()),
		valid_from: v.optional(v.string()),
		valid_to: v.optional(v.nullable(v.string())),
		status: v.optional(v.picklist(['success', 'error'])),
		error: v.optional(v.nullable(v.string())),
	})
}

export const getApplyTodsAcrossSignalsResponseSchema = (params: GenerateSchemaParams) => {
	return v.array(
		v.object({
			signal_id: v.string(),
			has_errors: v.boolean(),
			tods: v.array(
				v.object({
					tod: getOptimusTmsTodSchema(params),
					status: v.picklist(['inserted', 'error']),
					error: v.nullable(v.string()),
				}),
			),
		}),
	)
}

// ===================================================================
// ====================== Coordination Settings ======================
// ===================================================================

export const tms_coordination_settings_data = v.object({
	cycle: v.number(),
	offset: v.number(),
	splits: v.object({
		'1': v.number(),
		'2': v.number(),
		'3': v.number(),
		'4': v.number(),
		'5': v.number(),
		'6': v.number(),
		'7': v.number(),
		'8': v.number(),
		'9': v.number(),
		'10': v.number(),
		'11': v.number(),
		'12': v.number(),
		'13': v.number(),
		'14': v.number(),
		'15': v.number(),
		'16': v.number(),
	}),
	activePhases: v.array(v.number()),
	phaseSequence: v.object({
		ring_1: v.tuple([v.number(), v.number(), v.number(), v.number()]),
		ring_2: v.tuple([v.number(), v.number(), v.number(), v.number()]),
		ring_3: v.optional(v.tuple([v.number(), v.number(), v.number(), v.number()])),
		ring_4: v.optional(v.tuple([v.number(), v.number(), v.number(), v.number()])),
	}),
	offsetReferencePoint: v.picklist(['lead', 'lag']),
	clock_drift_adjustment: v.boolean(),
})

export const getOptimusTmsCoordinationSettingsSchema = (params: GenerateSchemaParams) => {
	return v.object({
		coordination_settings_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: 'coordination_settings_id',
		}),
		coordination_settings_external_id: v.string(),
		atlas_signal_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'atlas_signal_id' }),
		coordination_settings_data: tms_coordination_settings_data,
		valid_range: v.string(),
		valid_from: v.string(),
		valid_to: v.nullable(v.string()),
		status: v.optional(v.picklist(['success', 'error'])),
		error: v.optional(v.nullable(v.string())),
	})
}

export const getApplyCoordinationSettingsAcrossSettingsResponseSchema = (params: GenerateSchemaParams) => {
	return v.array(
		v.object({
			signal_id: v.string(),
			has_errors: v.boolean(),
			coordination_settings: v.array(
				v.object({
					coordination_setting: getOptimusTmsCoordinationSettingsSchema(params),
					status: v.picklist(['inserted', 'error']),
					error: v.nullable(v.string()),
				}),
			),
		}),
	)
}

// ============================================================
// ====================== Phase Settings ======================
// ============================================================

export const tms_phase_settings_data = v.record(
	v.string(),
	v.object({
		enabled: v.boolean(),
		maxSplit: v.nullable(v.number()),
		minSplit: v.number(),
		vehicleExt: v.number(),
		coordinated: v.boolean(),
		minimumGreen: v.number(),
		redClearance: v.number(),
		allowLeftTurn: v.boolean(),
		minPedWalkTime: v.number(),
		minPedClearance: v.number(),
		yellowClearance: v.number(),
		pedestrianPhase: v.optional(v.boolean()),
		dummyPhase: v.optional(v.boolean()),
	}),
)

export const getOptimusTmsPhaseSettingsSchema = (params: GenerateSchemaParams) => {
	return v.object({
		phase_settings_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'phase_settings_id' }),
		phase_settings_external_id: v.string(),
		atlas_signal_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'atlas_signal_id' }),
		phase_settings_data: tms_phase_settings_data,
		valid_range: v.string(),
		valid_from: v.optional(v.string()),
		valid_to: v.optional(v.nullable(v.string())),
		status: v.optional(v.picklist(['success', 'error'])),
		error: v.optional(v.nullable(v.string())),
	})
}

export const getApplyPhaseSettingsAcrossSettingsResponseSchema = (params: GenerateSchemaParams) => {
	return v.array(
		v.object({
			signal_id: v.string(),
			has_errors: v.boolean(),
			phase_settings: v.array(
				v.object({
					phase_setting: getOptimusTmsPhaseSettingsSchema(params),
					status: v.picklist(['inserted', 'error']),
					error: v.nullable(v.string()),
				}),
			),
		}),
	)
}

// =======================================================
// ====================== Sequences ======================
// =======================================================

export const tms_sequence_data = v.object({
	phaseSequence: v.object({
		ring_1: v.tuple([v.number(), v.number(), v.number(), v.number()]),
		ring_2: v.tuple([v.number(), v.number(), v.number(), v.number()]),
		ring_3: v.optional(v.tuple([v.number(), v.number(), v.number(), v.number()])),
		ring_4: v.optional(v.tuple([v.number(), v.number(), v.number(), v.number()])),
	}),
})

export const getOptimusTmsSequenceSchema = (params: GenerateSchemaParams) => {
	return v.object({
		sequence_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'sequence_id' }),
		sequence_external_id: v.string(),
		atlas_signal_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'atlas_signal_id' }),
		sequence_data: tms_sequence_data,
		valid_range: v.string(),
		valid_from: v.optional(v.string()),
		valid_to: v.optional(v.nullable(v.string())),
		status: v.optional(v.picklist(['success', 'error'])),
		error: v.optional(v.nullable(v.string())),
	})
}

export const getApplySequencesAcrossSettingsResponseSchema = (params: GenerateSchemaParams) => {
	return v.array(
		v.object({
			signal_id: v.string(),
			has_errors: v.boolean(),
			sequences: v.array(
				v.object({
					sequence: getOptimusTmsSequenceSchema(params),
					status: v.picklist(['inserted', 'error']),
					error: v.nullable(v.string()),
				}),
			),
		}),
	)
}

// =================================================================
// ====================== Controller Settings ======================
// =================================================================

export const tms_controller_settings_data = v.object({
	// TODO: convert to picklist
	forceOffMode: v.string(),
	offsetReferencePoint: v.string(),
})

export const getOptimusTmsControllerSettingsSchema = (params: GenerateSchemaParams) => {
	return v.object({
		controller_settings_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'controller_settings_id' }),
		controller_settings_external_id: v.string(),
		atlas_signal_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'atlas_signal_id' }),
		controller_settings_data: tms_controller_settings_data,
		valid_range: v.string(),
		valid_from: v.optional(v.string()),
		valid_to: v.optional(v.nullable(v.string())),
		status: v.optional(v.picklist(['success', 'error'])),
		error: v.optional(v.nullable(v.string())),
	})
}

export const getApplyControllerSettingsAcrossSettingsResponseSchema = (params: GenerateSchemaParams) => {
	return v.array(
		v.object({
			signal_id: v.string(),
			has_errors: v.boolean(),
			controller_settings: v.array(
				v.object({
					controller_setting: getOptimusTmsControllerSettingsSchema(params),
					status: v.picklist(['inserted', 'error']),
					error: v.nullable(v.string()),
				}),
			),
		}),
	)
}

// =============================================================
// ====================== Phase Mappings =======================
// =============================================================

export const getTmsAtomicMovementSchema = (params: GenerateSchemaParams) =>
	// TODO: Make ID not optional at some point
	v.object({
		key: v.optional(v.string()),
		id: v.optional(v.string()),
		name: v.optional(v.string()),
		type: v.optional(v.string()),
		edge_connection_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'edge_connection_id' }),
		priority: v.optional(v.string()),
		movement_type: v.optional(v.nullable(v.string())),
		is_permissive: v.optional(v.boolean()),
		is_rtor: v.optional(v.boolean()),
		overlap_name: v.optional(v.nullable(v.string())),
	})

export const getTmsPhaseMappingItemSchema = (params: GenerateSchemaParams) =>
	// TODO: Make ID not optional at some point
	v.object({
		id: v.optional(v.string()),
		key: v.optional(v.string()),
		name: v.optional(v.string()),
		number: v.number(),
		atomic_movements: v.array(getTmsAtomicMovementSchema(params)),
		priority: v.optional(v.string()),
	})

const getNewOrShortUuidSchema = (params: GenerateSchemaParams) =>
	v.union([
		ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'atlas_phase_mapping_id' }),
		v.pipe(v.string(), v.startsWith('new-')),
	])

export const getOptimusTmsPhaseMappingSchema = (params: GenerateSchemaParams) => {
	return v.object({
		atlas_phase_mapping_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: 'atlas_phase_mapping_id',
		}),
		phase_settings_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'phase_settings_id' }),
		phase_settings_external_id: v.optional(v.nullable(v.string())),
		atlas_signal_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'atlas_signal_id' }),
		phase_mapping: v.array(getTmsPhaseMappingItemSchema(params)),
		valid_range: v.optional(v.string()),
		valid_from: v.optional(v.string()),
		valid_to: v.optional(v.nullable(v.string())),
		status: v.optional(v.picklist(['success', 'error'])),
		error: v.optional(v.nullable(v.string())),
	})
}

// Write-path schema: same as read schema but tolerates `new-*` placeholder ids
// for rows that the FE has not yet persisted.
export const getOptimusTmsPhaseMappingWriteSchema = (params: GenerateSchemaParams) => {
	return v.object({
		atlas_phase_mapping_id: getNewOrShortUuidSchema(params),
		phase_settings_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'phase_settings_id' }),
		phase_settings_external_id: v.optional(v.nullable(v.string())),
		atlas_signal_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'atlas_signal_id' }),
		phase_mapping: v.array(getTmsPhaseMappingItemSchema(params)),
		valid_range: v.optional(v.string()),
		valid_from: v.optional(v.string()),
		valid_to: v.optional(v.nullable(v.string())),
		status: v.optional(v.picklist(['success', 'error'])),
		error: v.optional(v.nullable(v.string())),
	})
}

export const getArchivePhaseMappingRequestSchema = (params: GenerateSchemaParams) =>
	v.object({
		atlas_phase_mapping_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: 'atlas_phase_mapping_id',
		}),
		current_range: v.object({
			valid_from: v.string(),
			valid_to: v.nullable(v.string()),
		}),
		new_range: v.object({
			valid_from: v.string(),
			valid_to: v.nullable(v.string()),
		}),
	})

export const getDeletePhaseMappingRequestSchema = (params: GenerateSchemaParams) =>
	v.object({
		atlas_phase_mapping_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: 'atlas_phase_mapping_id',
		}),
		valid_from: v.string(),
		valid_to: v.nullable(v.string()),
	})
